import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import ExerciseLibrary from "@/pages/ExerciseLibrary";
import TrainingTracker from "@/pages/TrainingTracker";
import MealPlans from "@/pages/MealPlans";
import AICoach from "@/pages/AICoach";
import Inspiration from "@/pages/Inspiration";
import BodyTracker from "@/pages/BodyTracker";
import Profile from "@/pages/Profile";
import Navbar from "@/components/Navbar";
import UpgradePage from "@/pages/UpgradePage";
import AchievementsPage from "@/pages/AchievementsPage";
import GoalsPage from "@/pages/GoalsPage";
import SleepTrackerPage from "@/pages/SleepTrackerPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
// import DevicesPage from "@/pages/DevicesPage"; // Temporarily disabled
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminExercises from "@/pages/admin/AdminExercises";
import AdminInspiration from "@/pages/admin/AdminInspiration";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookiesPage from "@/pages/CookiesPage";
import TradingTermsPage from "@/pages/TradingTermsPage";
import CookieBanner from "@/components/CookieBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-[#DFFF00] font-mono text-sm animate-pulse">Indlæser...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("fitplan_token"));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const fetchSubscription = async (t) => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${t}` } });
      setSubscription(res.data);
    } catch { }
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setUser(res.data); fetchSubscription(token); setLoading(false); })
        .catch(() => { localStorage.removeItem("fitplan_token"); setToken(null); setUser(null); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("fitplan_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("fitplan_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("fitplan_token");
    setToken(null);
    setUser(null);
  };

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const refreshSubscription = () => { if (token) fetchSubscription(token); };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, authHeaders, subscription, refreshSubscription }}>
      <BrowserRouter>
        <div className="min-h-screen bg-[#09090b]">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/handelsbetingelser" element={<TradingTermsPage />} />
            <Route path="/upgrade" element={<ProtectedRoute><Navbar /><UpgradePage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Navbar /><Dashboard /></ProtectedRoute>} />
            <Route path="/exercises" element={<ProtectedRoute><Navbar /><ExerciseLibrary /></ProtectedRoute>} />
            <Route path="/training" element={<ProtectedRoute><Navbar /><TrainingTracker /></ProtectedRoute>} />
            <Route path="/meals" element={<ProtectedRoute><Navbar /><MealPlans /></ProtectedRoute>} />
            <Route path="/ai-coach" element={<ProtectedRoute><Navbar /><AICoach /></ProtectedRoute>} />
            <Route path="/inspiration" element={<ProtectedRoute><Navbar /><Inspiration /></ProtectedRoute>} />
            <Route path="/body-tracker" element={<ProtectedRoute><Navbar /><BodyTracker /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Navbar /><Profile /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Navbar /><AchievementsPage /></ProtectedRoute>} />
            <Route path="/goals" element={<ProtectedRoute><Navbar /><GoalsPage /></ProtectedRoute>} />
            <Route path="/sleep" element={<ProtectedRoute><Navbar /><SleepTrackerPage /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Navbar /><LeaderboardPage /></ProtectedRoute>} />
            {/* <Route path="/devices" element={<ProtectedRoute><Navbar /><DevicesPage /></ProtectedRoute>} /> */}
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/exercises" element={<AdminExercises />} />
            <Route path="/admin/inspiration" element={<AdminInspiration />} />
          </Routes>
          <CookieBanner />
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
